// This is the ported method used by the external NHLMIS REST API
const dateFns = require('date-fns')
const { warehouseCodeToVirtualId, translateAliases } = require('../../order/tools')
const { parseBatchId } = require('../../batch/api/batch-missing')
const createBulkUpsertBatchDetails = require('../../batch/upsert-batch-details')
const { classifyBatchRows } = require('../../batch/tools')
const getPeriodFromProgram = require('../../report/tools/get-period-from-program')
const { validNhlmisRestApiSubmit } = require('../../validate/validators')
const shouldTrackBatches = require('../../tools/should-track-batches')
const { stockWithAmounts } = require('../../tools/doc-to-stock-count-record')

const STOCK_COUNT_DOC_TYPE = 'stock-count'

const normalizeExpiry = (expiry) => {
  return new Date(expiry).toISOString()
}

const canSubmit = (userLocation, locationId) => {
  return userLocation === locationId || // 1. can submit for your own location
    userLocation === 'national' || // 2. national can submit for everything
    locationId.indexOf(userLocation) === 0 // 3. user location is included in location id
}

const pluckBatchIds = (stockCounts) => {
  return Object.keys(stockCounts)
    .reduce((batchIds, productId) => {
      const productBatches = Object.keys(stockCounts[productId].batches || {})
      return batchIds.concat(productBatches)
    }, [])
}

const inputToFields = (item) => {
  return {
    fields: {
      'field:standard-physical-count': {
        amount: item.amount
      }
    }
  }
}

// Sets commits.allocated:<funderId> = total physical count for every product in the stock.
// This keeps the funder allocation commit in sync with what mSupply actually synced,
// so order creation (which reads commits.allocated:<funderId>.amount as available stock)
// always reflects the current physical quantity rather than a stale value from a previous
// bulk allocation.
const addFunderCommits = (formattedStock, funderId) => {
  if (!funderId) return formattedStock
  // stockWithAmounts aggregates batch fields into a top-level amount per product,
  // reusing the same logic used elsewhere in the report pipeline.
  const withAmounts = stockWithAmounts(formattedStock)
  for (const productId of Object.keys(formattedStock)) {
    formattedStock[productId].commits = {
      [`allocated:${funderId}`]: { amount: (withAmounts[productId] || {}).amount || 0 }
    }
  }
  return formattedStock
}

const convertFields = (stock, alias) => {
  const items = Object.keys(stock)
  const converted = {}

  items.forEach(item => {
    if (stock[item].batches) {
      converted[item] = {
        batches: convertFields(stock[item].batches)
      }
    } else {
      converted[item] = inputToFields(stock[item])
    }
  })

  return converted
}

/*
 * Check that all the products
 * belong to the submitted service
 * and units and names match
 */
const validateProducts = (stock, availableProducts, alias) => {
  const availableProductsById = availableProducts.reduce((acc, product) => {
    if (alias !== 'none') {
      if (product.alias && Array.isArray(product.alias[alias])) {
        for (const itemId of product.alias[alias]) {
          acc.add(`product:${itemId}`.toLowerCase())
        }
      }
    } else {
      acc.add(product._id)
    }
    return acc
  }, new Set())

  const invalidProducts = Object.keys(stock)
    .filter(submittedId => {
      return !availableProductsById.has(submittedId.toLowerCase())
    })

  return invalidProducts.length
    ? invalidProducts
    : null
}

const batchesNeeded = ({location, stock}) => {
  const needsBatches = []
  const shouldTrack = shouldTrackBatches({location: { id: location }})
  if (shouldTrack) {
    Object.keys(stock).forEach(productId => {
      if (!stock[productId].batches) {
        needsBatches.push(productId)
      }
    })
  }

  if (needsBatches.length === 0) {
    return false
  }

  return needsBatches
}

const badRequestError = (message) => {
  const err = new Error(message)
  err.status = 400
  return err
}

const toResponseShape = (submittedStock, stockCount, {alias, location, funderId}) => {
  return {
    id: stockCount._id,
    // In case the submitted stock was aliased, it will simply be echoed
    // back instead of converting the submission back into itself
    stock: alias !== 'none' ? submittedStock.stock : stockCount.stock,
    date: dateFns.format(new Date(stockCount.updatedAt), 'YYYY-MM-DD'),
    funderId,
    version: stockCount.version,
    serviceId: stockCount.serviceId,
    warehouseCode: location && location.additionalData && location.additionalData.warehouseCode
  }
}

const prepareStockCountWith = (user, api) => async (submittedStock, {alias, mergeBatches, useWarehouseCodes, warehouseLocationId, tenant}) => {
  let locationId
  const {warehouseCode, funderId, programId} = submittedStock
  try {
    locationId = useWarehouseCodes
      ? warehouseCodeToVirtualId({warehouseCode, funderId, programId, tenant})
      : submittedStock.locationId || warehouseLocationId
  } catch (err) {
    err.status = 400
    throw err
  }
  const location = locationId ? await api.location.get(locationId) : await api.location.getByWarehouseCode(warehouseCode)
  if (!location) {
    const err = new Error(
      `Invalid location "${useWarehouseCodes ? `${submittedStock.warehouseCode} (${locationId})` : submittedStock.locationId}"`
    )
    err.status = 400
    return Promise.reject(err)
  }

  if (!canSubmit(user.location.id, location._id)) {
    const err = new Error(
      `You are not allowed to submit for "${useWarehouseCodes ? `${submittedStock.warehouseCode} (${locationId})` : submittedStock.locationId}"`
    )
    err.status = 403
    return Promise.reject(err)
  }

  submittedStock.location = location._id
  delete submittedStock.warehouseCode

  const [products, batchesNotInDB] = await Promise.all([
    // This is used to check whether the submitted products actually
    // belong to the specified program / service
    api.configuration
      .getByIds(
        [`configuration:${submittedStock.service}`],
        {includeProducts: true}
      )
      .then(configs => {
        if (configs.length === 0) {
          const err = new Error(
            `Configuration "configuration:${submittedStock.service}" not found. This probably means the given "programId" does not exist.`
          )
          err.status = 400
          return Promise.reject(err)
        }
        return configs[0].products
      }),
    // This checks if all submitted batches already exist in the database
    api.batch.missing(
      pluckBatchIds(submittedStock.stock),
      alias !== 'none' ? {resolveAlias: {[alias]: true}} : {}
    )
  ])
  if (batchesNotInDB) {
    return Promise.reject(badRequestError(
      `Invalid batches: ${batchesNotInDB.join(', ')} need to be added via the batches API beforehand`
    ))
  }

  const shouldSubmitBatches = batchesNeeded(submittedStock)
  if (shouldSubmitBatches) {
    return Promise.reject(badRequestError(
      `You need to report batches for these products before submitting: "${shouldSubmitBatches.join(', ')}"`
    ))
  }

  const misplacedProducts = validateProducts(submittedStock.stock, products, alias)
  if (misplacedProducts) {
    return Promise.reject(badRequestError(
      `Invalid products: "${misplacedProducts.join(', ')}" for "${submittedStock.service}"`
    ))
  }

  const service = await api.service.get(submittedStock.service)
  const period = getPeriodFromProgram(service.program, submittedStock.date || new Date(), true)
  const stock = translateAliases(submittedStock.stock, alias, products)
  const formattedStock = addFunderCommits(convertFields(stock, alias), funderId)

  let submittedAt
  if (!mergeBatches) {
    submittedAt = new Date()
  }

  return {
    saveParams: {
      locationId: location._id,
      service,
      stock: formattedStock,
      period,
      submittedAt,
      mergeCommits: true,
      mergeBatches
    },
    submittedStock,
    location,
    funderId: submittedStock.funderId
  }
}

module.exports = nhlmisRestSubmit
module.exports.addFunderCommits = addFunderCommits
module.exports.normalizeExpiry = normalizeExpiry
async function nhlmisRestSubmit (stockCounts, {alias = 'one', mergeBatches = false, useWarehouseCodes = true, warehouseLocationId = null, tenant = 'PSM', enableBatchCreation = false} = {}) {
  if (!Array.isArray(stockCounts)) {
    const err = new Error('No `stockCounts` key found on payload or `stockCounts` is not an array')
    err.status = 400
    throw err
  }

  for (const stockCount of stockCounts) {
    // API Users will only ever submit data for warehouse services
    Object.assign(stockCount, {
      type: STOCK_COUNT_DOC_TYPE,
      service: `${stockCount.programId}:service:warehouse`
    })
  }

  // Classify and upsert new batches / existing batch updates when enabled
  if (enableBatchCreation) {
    for (const stockCount of stockCounts) {
      const classificationRows = []

      for (const [productId, productData] of Object.entries(stockCount.stock)) {
        const batches = productData.batches || {}
        for (const [batchId, batchData] of Object.entries(batches)) {
          const isNew = batchId.startsWith('__new_')
          classificationRows.push({
            productid: productId,
            batchid: isNew ? '' : batchId,
            batchnumber: batchData.batchnumber || '',
            expirydate: batchData.expirydate || '',
            manufacturer: batchData.manufacturer || '',
            _tempKey: isNew ? batchId : undefined
          })
        }
      }

      const { newBatches, batchUpdates } = classifyBatchRows(classificationRows)

      const bulkUpsert = createBulkUpsertBatchDetails({ mainApi: this.mainApi, logger: console })
      const bulkItems = [
        ...newBatches.map(b => ({
          skuCode: b.productId.replace('product:', ''),
          batchNo: b.batchNo,
          manufacturer: b.manufacturer,
          expiry: normalizeExpiry(b.expiry)
        })),
        ...batchUpdates.map(({ batchId: bid, expiry }) => {
          const { productId, manufacturer, batchNo } = parseBatchId(bid)
          return {
            skuCode: productId.replace('product:', ''),
            batchNo,
            manufacturer,
            expiry: normalizeExpiry(expiry)
          }
        })
      ]

      if (bulkItems.length > 0) {
        await bulkUpsert(bulkItems)
      }

      // Replace temp batch keys with generated IDs in the stock object
      for (const row of classificationRows) {
        if (!row.batchid || !row._tempKey) continue
        const productData = stockCount.stock[row.productid]
        if (!productData || !productData.batches) continue
        const tempBatch = productData.batches[row._tempKey]
        if (tempBatch) {
          delete productData.batches[row._tempKey]
          productData.batches[row.batchid] = tempBatch
        }
      }

      // Strip raw batch fields so validation doesn't reject unknown keys
      for (const productData of Object.values(stockCount.stock)) {
        if (productData.batches) {
          for (const batch of Object.values(productData.batches)) {
            delete batch.batchnumber
            delete batch.expirydate
            delete batch.manufacturer
          }
        }
      }
    }
  }

  const validationErrors = stockCounts
    .map(validNhlmisRestApiSubmit)
    .filter(v => v)

  if (validationErrors.some(list => list.length)) {
    const err = new Error('Validating the payload against the given schema failed.')
    err.status = 400
    Object.assign(err, {errors: validationErrors})
    throw err
  }

  const prepareStockCount = prepareStockCountWith(this.mainApi.state.user, this.mainApi)
  const preparedDocs = await Promise.all(
    stockCounts.map(stockCount =>
      prepareStockCount(stockCount, {alias, mergeBatches, useWarehouseCodes, warehouseLocationId, tenant})
    )
  )

  // Collapse docs targeting the same location+service+period into one save
  // to avoid concurrent read-modify-write conflicts on the same document.
  const collapseKey = ({saveParams}) => `${saveParams.locationId}|${saveParams.service.id}|${saveParams.period.id}`
  const collapsed = new Map()
  for (const doc of preparedDocs) {
    const key = collapseKey(doc)
    if (collapsed.has(key)) {
      const existingStock = collapsed.get(key).saveParams.stock
      for (const [productId, productData] of Object.entries(doc.saveParams.stock)) {
        if (existingStock[productId]) {
          // Merge individual batches — each stock count may contribute different batches for the same product
          Object.assign(existingStock[productId].batches, productData.batches)
          // Sum amounts for the same funder key; add a new key for a different funder
          const existingCommits = existingStock[productId].commits || {}
          for (const [funderId, commitData] of Object.entries(productData.commits || {})) {
            if (existingCommits[funderId]) {
              existingCommits[funderId] = { amount: (existingCommits[funderId].amount || 0) + (commitData.amount || 0) }
            } else {
              existingCommits[funderId] = commitData
            }
          }
          existingStock[productId].commits = existingCommits
        } else {
          existingStock[productId] = productData
        }
      }
    } else {
      collapsed.set(key, {...doc, saveParams: {...doc.saveParams}})
    }
  }

  const savedByKey = new Map()
  await Promise.all(
    [...collapsed.entries()].map(async ([key, doc]) => {
      try {
        const stockCount = await this.mainApi.report.save(doc.saveParams)
        savedByKey.set(key, stockCount)
      } catch (e) {
        console.error(`Error saving stock count document: ${e.docId || 'unknown id'}!`)
        console.error(doc.saveParams)
        console.error(e)
        throw e
      }
    })
  )

  return preparedDocs.map(doc =>
    toResponseShape(doc.submittedStock, savedByKey.get(collapseKey(doc)), {
      alias,
      location: doc.location,
      funderId: doc.funderId
    })
  )
}
